// @flow
import React, { Fragment, useState } from 'react';
import { isBrowser } from 'react-device-detect';
import { Link } from 'react-router-dom';
import Button from 'components/common/Button';
import MarkdownRender from 'components/common/MarkdownRender';
import ProductPrice from 'components/product/ProductPrice';
import ProductSequencesContainer from 'containers/product/ProductSequencesContainer';
import ProductSimilarContainer from 'containers/product/ProductSimilarContainer';
import TrendingEventCards from 'containers/list/TrendingEventCards';
import Tooltip from 'react-tooltip';
import cx from 'classnames';
import { MdLock, MdChat } from 'react-icons/md';
import { resizeImage, numberWithCommas } from 'lib/common';
import cloneDeep from 'lodash/cloneDeep';

import './ProductContent.scss';
import ProductMobileShare from '../ProductMobileShare';
import ProductLikeButton from '../ProductLikeButton';
import ProductActionButtons from '../ProductActionButtons';
import ProductTags from '../ProductTags';

type Props = {
  body: string,
  theme: string,
  thumbnail: ?string,
  onChannel: void,
  price: string,
  supplier: string,
  productUrl: string,
  productPrice: string,
  displayName: string,
  tags: string,
  gradeDiscount: number,
  refreshProductData: () => Promise<boolean>,
  shortDescription: ?string,
};

const ProductContent = ({
  isPrivate,
  title,
  url,
  user,
  informCopy,
  onToggleLike,
  likes,
  liked,
  logged,
  ownProduct,
  product,
  id,
  onAskRemove,
  body,
  thumbnail,
  theme,
  price,
  supplier,
  productUrl,
  onLogin,
  productPrice,
  onChannel,
  username,
  displayName,
  currentUsername,
  tags,
  categories,
  gradeDiscount,
  badges,
  refreshProductData,
  shortDescription,
}: Props) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageAspectRatio, setImageAspectRatio] = useState(null);

  const handleImageLoad = e => {
    const img = e.target;
    const aspectRatio = img.naturalWidth / img.naturalHeight;
    setImageAspectRatio(aspectRatio);
    setImageLoaded(true);
  };

  const getImageClass = () => {
    if (!imageLoaded || !imageAspectRatio) return 'loading';
    if (imageAspectRatio > 1.5) {
      return 'landscape'; // 가로가 긴 이미지
    }
    if (imageAspectRatio < 0.7) {
      return 'portrait'; // 세로가 긴 이미지
    }
    return 'square'; // 정사각형에 가까운 이미지
  };

  // 깊은 복사 (리랜더링 되도라도 새로 다시 계산)
  const copyProductPrice = cloneDeep(productPrice);

  // eslint-disable-next-line no-return-assign,array-callback-return
  copyProductPrice.map(item => {
    item.price = item.price.toFixed(0);
    item.price_vat = (item.price * 1.1).toFixed(0);
  });

  const sortCategories = categories.sort((a, b) => {
    return b.order - a.order;
  });
  const urlCategories = sortCategories.length ? sortCategories[0].url_slug : '/categories';

  return (
    <div className="ProductContent">
      <div className="mini-product-card">
        {thumbnail && (
          <Fragment>
            <div className="thumbnail">
              <img
                src={thumbnail}
                alt={title}
                loading="lazy"
                className={getImageClass()}
                onLoad={handleImageLoad}
              />
            </div>
            <div className="separator" />
          </Fragment>
        )}
        <div className="product-info">
          <div className="header">
            <Link
              to={`/search?q=&menu=products&facets=${encodeURIComponent(
                JSON.stringify({ brand: supplier }),
              )}`}
              className="supplier-name"
            >
              {supplier}
            </Link>
            {
              <div className="private-options">
                {isPrivate && (
                  <div className="closed-door">
                    <MdLock />
                    비공개
                  </div>
                )}
              </div>
            }
          </div>
          <h1 className="title">{title}</h1>
          <div className="badges-share">
            <div className="badges">
              {badges.slice(0, 3).map(badge => (
                <Link
                  key={badge.name}
                  to={`/search?q=&menu=products&facets=${encodeURIComponent(
                    JSON.stringify({ badges: badge.name }),
                  )}`}
                  className="badge"
                  style={{ backgroundColor: badge.color }}
                >
                  <div className="text">{badge.name}</div>
                </Link>
              ))}
            </div>
            <div className="share-and-likes">
              <ProductLikeButton
                onClick={onToggleLike}
                liked={liked}
                likes={likes}
                disabled={!logged}
              />
              <ProductMobileShare
                url={url}
                title={title}
                username={user.username}
                informCopy={informCopy}
              />
            </div>
          </div>
          {/* <div className="banner">
            <Link to={'/@cacheby/banners/연구비-카드-결제-오픈-vckem7meaz'}>
              <img
                className="research-card"
                src={`${process.env.IMAGES_URL}/banner-images/cacheby/research-card.png`}
                alt="research-card-banner"
              />
            </Link>
          </div> */}
          <div className="separator-x" />
          <div className="sale-brand">
            {shortDescription && (
              <div className="ai-summary">
                <div className="ai-summary-header">
                  <span className="ai-icon" role="img" aria-label="star">
                    ✦
                  </span>
                  <span className="ai-title">상품 한눈에 보기</span>
                </div>
                <p className="ai-summary-text">{shortDescription}</p>
              </div>
            )}
            <div className="brand-text">
              <strong>브랜드: </strong>
              <Link
                to={`/search?q=&menu=products&facets=${encodeURIComponent(
                  JSON.stringify({ brand: supplier }),
                )}`}
                className="supplier-name"
              >
                {supplier}
              </Link>
            </div>
            <div className="brand-btn">
              <Button
                theme="outline"
                to={`/search?q=&menu=products&facets=${encodeURIComponent(
                  JSON.stringify({ categories: urlCategories }),
                )}`}
              >
                카테고리 상품 전체보기
              </Button>
              <Button
                theme="outline"
                to={`/search?q=&menu=products&facets=${encodeURIComponent(
                  JSON.stringify({ brand: supplier }),
                )}`}
              >
                브랜드 상품 전체보기
              </Button>
            </div>
          </div>
          <div className="separator-x" />
          <div className="buttons">
            <Button
              theme="default"
              color="blue"
              className="button"
              onClick={() => onChannel(productPrice[0], '상세 페이지')}
              desktop={isBrowser}
            >
              제품상담
            </Button>
            {badges && badges.some(badge => badge.name === '데모') && (
              <Button
                theme="default"
                color="violet"
                className="button"
                onClick={() =>
                  window.open(
                    'https://docs.google.com/forms/d/e/1FAIpQLSdIiOOXsXdchmXIp8HeJQWQB5g0FJrNUCJDSYQzdC0YNpVlpQ/viewform',
                  )
                }
                desktop={isBrowser}
              >
                데모신청
              </Button>
            )}
            {badges && badges.some(badge => badge.name === '샘플') && (
              <Button
                theme="default"
                color="sample"
                className="button"
                onClick={() =>
                  window.open(
                    'https://docs.google.com/forms/d/1ct9abDGyBh4ZjQwODCestkKqvm5gZKDobNuLogGZ2H4/edit',
                  )
                }
                desktop={isBrowser}
              >
                샘플신청
              </Button>
            )}
            {ownProduct && (
              <Button
                theme="default"
                color="green"
                className="button"
                onClick={() => window.open(productUrl)}
                desktop={isBrowser}
              >
                홈페이지
              </Button>
            )}
          </div>
          <ProductTags tags={tags} />
          <Tooltip effect="solid" className="tooltip" />
        </div>
      </div>
      <TrendingEventCards />
      {copyProductPrice.length !== 0 && (
        <ProductPrice
          logged={logged}
          currentUsername={currentUsername}
          user={user}
          onLogin={onLogin}
          productId={id}
          productTitle={title}
          productBrand={supplier}
          productPrice={copyProductPrice}
          onChannel={onChannel}
          refreshProductData={refreshProductData}
          supplier={supplier}
        />
      )}
      <ProductSimilarContainer productId={id} />
      <div className="contents">
        <MarkdownRender body={body} theme={theme || 'github'} />
      </div>
      <ProductSequencesContainer username={username || ''} supplier={supplier} />
    </div>
  );
};

export default ProductContent;



// WEBPACK FOOTER //
// ./src/components/product/ProductContent/ProductContent.js